import RosterBackendService from './RosterBackendService';
import {
    SessionsGetRequest,
    ProvisionAcademicYearRequest,
    SiteDTO,
    ProvisionedEntitiesRequest,
    SelectionsResponse,
    ProvisionTreeNodeDTO,
    SiteTypes,
    CourseListItem,
    CurrentAndFutureAcademicYearsResponse,
    CurrentAcademicSessionDTO,
    CustomerDTO,
    AcademicSessionDTO,
    SchoolListItem,
    ProductDTO,
    SiteProductDTO,
    ProvisionAction,
    AcademicYearDTO
} from '@beable/roster-backend-client-v1';

async function getCustomers(): Promise<CustomerDTO[] | null> {
    const response = await RosterBackendService.getCustomers();
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function fetchAllSites(): Promise<SiteDTO[]> {
    let sites: SiteDTO[] = [];
    const response = await RosterBackendService.getCustomers();
    if (response.isOk()) {
        const mapped = response.value.map(s => s.id);
        const sl = mapped.length;
        for (let i = 0; i < sl; i++) {
            const id = mapped[i];
            const siteArr = await getSitesForCustomer(id);
            if (siteArr) {
                sites = sites.concat(siteArr);
            }
        }
    }

    return sites;
}

async function getSitesForCustomer(customerId: string): Promise<SiteDTO[] | null> {
    const response = await RosterBackendService.getSitesForCustomer(customerId);
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getSitesById(siteId: number): Promise<SiteDTO | null> {
    const response = await RosterBackendService.getSiteById(siteId);
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getSitesByType(siteTypes: SiteTypes[]): Promise<SiteDTO[] | null> {
    const response = await RosterBackendService.getSitesByType({ types: siteTypes });
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getSessions(siteId: number): Promise<AcademicSessionDTO[] | null> {
    if (!siteId) {
        return null;
    }

    const response = await RosterBackendService.getSessions(siteId);
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getSchoolYears(siteId: number): Promise<AcademicSessionDTO[] | null> {
    if (!siteId) {
        return null;
    }

    const response = await RosterBackendService.getSchoolYears(siteId);
    if (response.isOk()) {
        response.value.forEach((session: any) => {
            const endDate = new Date(session.endDate);
            const dateOffset = endDate.getTimezoneOffset();

            const localDate = endDate.getTime() + dateOffset * 60 * 1000;
            session.endDate = new Date(localDate);
        });

        return response.value;
    }

    return null;
}

async function getSchools(siteId: number): Promise<SchoolListItem[] | null> {
    const response = await RosterBackendService.getSchools(siteId);
    if (response.isOk()) {
        return response.value;
    }
    return null;
}

async function getProducts(customerUuid: string): Promise<ProductDTO[] | null> {
    if (!customerUuid) {
        return null;
    }

    const response = await RosterBackendService.getProductsForCustomer(customerUuid);
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getSiteProducts(customerId: string, siteId: number): Promise<SiteProductDTO[] | null> {
    if (!customerId || !siteId) {
        return null;
    }

    const response = await RosterBackendService.getSiteProducts({ customerId, siteId });
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getNodes(siteId: number, academicYearId: string): Promise<ProvisionTreeNodeDTO[] | null> {
    if (!siteId || !academicYearId) {
        return null;
    }

    const response = await RosterBackendService.getRoster({ siteId, academicYearId });
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

/**
 * Get the node children of a specified node or the root (if no node was specified)
 * from the provision tree for the specified site and academic year.
 *
 * @param {number} siteId
 * @param {string} academicYearId
 * @param {string} [nodeValue='']
 * @param {boolean} [appendChildren=false]
 * @return {*}  {(Promise<(ProvisionTreeNodeDTO & { warning?: string })[] | null >)}
 */
async function getProvisionNodes(
    siteId: number,
    academicYearId: string,
    nodeValue: string = '',
    appendChildren: boolean = false
): Promise<(ProvisionTreeNodeDTO & { warning?: string })[] | null> {
    if (!siteId || !academicYearId) {
        return null;
    }
    const response = await RosterBackendService.getProvisionNodes({ siteId, academicYearId, nodeValue, appendChildren });
    if (response.isOk()) {
        return response.value;
    }
    return null;
}

/**
 * Get a specified root node (with its child nodes if argument appendChildren is true)
 * from the provision tree for the specified site and academic year.
 *
 * @param {number} siteId
 * @param {string} academicYearId
 * @param {string} nodeValue
 * @param {boolean} [appendChildren=true]
 * @return {*} 
 */
async function getRootNode(
    siteId: number,
    academicYearId: string,
    nodeValue: string,
    appendChildren: boolean = true
): Promise<ProvisionTreeNodeDTO | null> {
    if (!siteId || !academicYearId || !nodeValue) {
        return null;
    }
    const response = await RosterBackendService.getRootNode({ siteId, academicYearId, nodeValue, appendChildren });
    if (response.isOk()) {
        return response.value;
    }
    return null;
}

async function getCourseCodes(siteId: number, orgId?: string): Promise<ProvisionTreeNodeDTO[]> {
    if (!siteId) {
        return [];
    }

    const response = await RosterBackendService.getCourseCodesForSite({ siteId, orgId });
    if (response.isOk()) {
        return response.value;
    }

    return [];
}

async function getCourses(siteId: number): Promise<CourseListItem[]> {
    if (!siteId) {
        return [];
    }

    const response = await RosterBackendService.getCoursesBySiteId({ siteId });
    if (response.isOk()) {
        return response.value;
    }

    return [];
}

async function getAcademicYears(siteId: number): Promise<CurrentAndFutureAcademicYearsResponse | null> {
    const response = await RosterBackendService.getCurrentAndFutureAcademicYears({ siteId });
    if (response.isOk()) {
        return response.value;
    }
    return null;
}

async function getPreviousAcademicYear(siteId: number): Promise<AcademicYearDTO | null> {
    const response = await RosterBackendService.getPreviousAcademicYear({ siteId });
    if (response.isOk()) {
        return response.value;
    }
    return null;
}

async function getPreSelectedNodes(siteId: number, academicYearId: string, productId: string): Promise<SelectionsResponse | string[] | null> {  // TODO: remove string[]. Added for backwards compatability
    if (!siteId || !academicYearId || !productId) {
        return null;
    }
    const response = await RosterBackendService.getPreSelectedNodes({ siteId, productId, academicYearId });
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function saveSelectedNodes(siteId: number, academicYearId: string, productId: string, checkedNodes: any[]): Promise<boolean | null> {
    if (!siteId || !academicYearId || !productId) {
        return null;
    }

    const requestData = {
        siteId,
        productId,
        academicYearId,
        selections: JSON.stringify(checkedNodes),
    };

    const response = await RosterBackendService.saveSelectedNodes(requestData);
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getCurrentAndFutureSchoolYears(siteId: number, orgId?: string): Promise<CurrentAcademicSessionDTO[] | boolean> {
    const request: SessionsGetRequest = { siteId, orgId }
    const response = await RosterBackendService.getCurrentAndFutureSchoolYears(request);
    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function provisionAcademicYear(siteId: number, academicYearId: string, productId: string, action?: ProvisionAction): Promise<boolean> {
    const request: ProvisionAcademicYearRequest = new ProvisionAcademicYearRequest();
    request.siteId = siteId;
    request.academicYearId = academicYearId;
    request.productId = productId;
    if (action) { request.action = action; }
    const response = await RosterBackendService.provisionAcademicYear(request);
    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function getProvisionedClasses(siteId: number, academicYearId: string, userId: string): Promise<string[]> {
    let provisionedClasses: string[] = [];
    const req: ProvisionedEntitiesRequest = {
        siteId: siteId,
        academicYearId: academicYearId,
        userIds: [userId]
    }
    const response = await RosterBackendService.getProvisionedClasses(req);
    if (response.isOk()) {
        provisionedClasses = response.value?.classIds;
    }
    return provisionedClasses;
}

export default {
    getCustomers,
    getNodes,
    getCourseCodes,
    getCourses,
    getAcademicYears,
    getPreviousAcademicYear,
    getPreSelectedNodes,
    getProducts,
    getSiteProducts,
    getSessions,
    getSchools,
    getSchoolYears,
    getSitesForCustomer,
    saveSelectedNodes,
    getCurrentAndFutureSchoolYears,
    provisionAcademicYear,
    fetchAllSites,
    getSitesById,
    getSitesByType,
    getProvisionedClasses,
    getProvisionNodes,
    getRootNode,
}
